import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheckIcon as ShieldCheck, SparkleIcon as Sparkle, ScalesIcon as Scales } from "@phosphor-icons/react";

import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicShell from "@/components/PublicShell";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { RowListSkeleton } from "@/components/SkeletonList";
import VerifiedStandardsBadge from "@/components/VerifiedStandardsBadge";

/**
 * Public seller-standards landing page.
 *
 * Renders the current published version with a clear preface, every
 * checklist item grouped by section, the legal disclaimer, and an
 * "Earn the badge" CTA for signed-in sellers.
 */
export default function SellerStandards() {
  const { user } = useAuth() || {};
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/seller-standards/current")
      .then((r) => alive && setDoc(r.data))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const totalItems = useMemo(
    () => (doc?.sections || []).reduce((acc, s) => acc + (s.items?.length || 0), 0),
    [doc],
  );
  const Shell = user ? AppShell : PublicShell;
  return (
    <Shell>
      <article className="max-w-3xl mx-auto" data-testid="seller-standards-page">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.18em] text-gold font-bold">
          <Scales size={12} weight="fill" />
          Seller standards · {doc?.version || "—"}
        </div>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight text-ink mb-4">
          Standards we <span className="italic">strongly encourage</span>
        </h1>
        <p className="text-[15px] text-[#3F3F3F] leading-relaxed mb-6 max-w-2xl">
          {doc?.preface || (
            "Armstrong K9 Registry strongly encourages every seller to follow the standards below…"
          )}
        </p>

        {doc?.venue_statement && (
          <div
            className="rounded-2xl bg-[#0E0E0E] text-white/85 p-5 mb-6 max-w-2xl"
            data-testid="standards-venue-statement"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gold mb-2">
              The Registry's role · venue
            </p>
            <p className="text-[13px] leading-relaxed">{doc.venue_statement}</p>
          </div>
        )}

        <div
          className="rounded-2xl bg-[#FBF7EC] border border-[#E8D9A6] text-[#8A6A00] p-4 mb-6 text-[12.5px] leading-relaxed"
          data-testid="standards-venue-banner"
        >
          <strong>Buyer beware &mdash; you transact at your own risk.</strong>{" "}
          Armstrong K9 Registry is a <strong>venue</strong>. The standards
          below are guidance + a self-attestation framework only. We do{" "}
          <strong>not</strong> independently audit compliance, do{" "}
          <strong>not</strong> warrant a seller's adherence, and are{" "}
          <strong>not</strong> a party to any sale arranged on the platform.
          Request written records, ask for a vet exam, and independently
          verify the seller before paying. The optional escrow service holds
          funds for <strong>14 days</strong> after pickup as a dispute
          window, but Armstrong does not unilaterally refund money once
          released and does not guarantee the dog, lineage, health, or
          temperament.
        </div>

        {!loading && user && (
          <div className="rounded-2xl border border-[#0F8043]/30 bg-[#F1FAF3] p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4 mb-8" data-testid="standards-cta">
            <div className="h-10 w-10 rounded-full bg-[#0F8043]/10 text-[#0F8043] flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={20} weight="fill" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-ink mb-1">Earn the Verified Standards badge</h2>
              <p className="text-[13px] text-[#3F3F3F] leading-relaxed">
                Tick all {totalItems || 15} items once and you'll display a public badge on your breeder card, every listing, and every escrow receipt — clearly differentiating you from sellers who haven't filed an attestation.
              </p>
            </div>
            <Link to="/seller-standards/attest" data-testid="standards-attest-link">
              <Button className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white h-10 px-5">
                <Sparkle size={14} weight="fill" className="mr-1.5" />
                Attest now
              </Button>
            </Link>
          </div>
        )}

        {loading && <RowListSkeleton />}

        {!loading && (doc?.sections || []).map((sec) => (
          <section key={sec.id} className="mb-8" data-testid={`standards-section-${sec.id}`}>
            <div className="flex items-baseline justify-between gap-2 mb-3">
              <h2 className="font-spectral text-2xl tracking-tight text-ink">{sec.title}</h2>
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-gold flex-shrink-0">
                {sec.encouragement}
              </span>
            </div>
            <ol className="space-y-2.5">
              {(sec.items || []).map((it, idx) => (
                <li
                  key={it.id}
                  data-testid={`standards-item-${it.id}`}
                  className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex gap-3"
                >
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#FBF7EC] text-[#8A6A00] flex items-center justify-center text-[11px] font-bold">
                    {idx + 1}
                  </span>
                  <span
                    className="text-[14px] text-ink leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: (it.label || "").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>"),
                    }}
                  />
                </li>
              ))}
            </ol>
          </section>
        ))}

        {!loading && doc && (
          <section className="rounded-2xl bg-[#0E0E0E] text-white/85 p-5 sm:p-6 mb-8" data-testid="standards-disclaimer">
            <h2 className="font-spectral text-xl text-white tracking-tight mb-2 flex items-center gap-2">
              <Scales size={18} weight="duotone" className="text-gold" />
              Important legal notice
            </h2>
            <p className="text-[12.5px] leading-relaxed">{doc.legal_disclaimer}</p>
          </section>
        )}

        {!loading && doc && (
          <p className="text-[12px] text-[#6B6B6B] flex items-center gap-2">
            <VerifiedStandardsBadge size="xs" /> appears next to sellers who have self-attested to the full checklist above. Effective {doc.effective_date}.
          </p>
        )}
      </article>
    </Shell>
  );
}
